import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { find, sortBy, isEqual } from 'lodash-es';
import { get, post } from '../../../lib/api';
import baseSupportingLayers from './supporting-layers';
import { ImageOverlay } from './image-overlay';
import { useMapProperties } from './use-map-properties';
import { useHasChanged } from '../../../hooks';
import { usePermission } from '../../../hooks/use-permission';
import { usePermissions } from '../../../hooks/use-permissions';
import { useCurrentUser } from '../../../hooks/use-current-user';
import { hasChartColors, setChartColors } from '../../../services/chart-colors';

const MIN_ZOOM = 11;
const CLICKABLE = [
  'boundary',
  'boundary-uncertified',
  'irrigation-boundary',
  'entrance',
  'aa-grid',
  'h3-grid',
];

export function useLayers(props) {
  const {
    map,
    config,
    filters,
    selectedFeatures,
    onClick,
    layerVisibilities: visibilities,
    pkRemovalEnabled,
  } = props;

  const visibilitiesChanged = useHasChanged(visibilities);
  const hasTurnedOnVisibleLayers = useRef(false);
  const { extent, size, zoom } = useMapProperties(map);
  const [layers, setLayers] = useState([]);
  const [disabledLayers, setDisabledLayers] = useState([]);
  const [loadingLayers, setLoadingLayers] = useState([]);
  const [visibleLayers, setVisibleLayers] = useState([]);
  const showGrids = usePermission('ADMIN_MAP');
  const permissions = usePermissions();
  const currentUser = useCurrentUser();

  const supportingLayers = useMemo(
    () =>
      baseSupportingLayers({
        showPkRemoval: pkRemovalEnabled,
        showGrids,
        permissions,
      }).map((layer) => ({
        ...layer,
        hidden: false,
      })),
    [pkRemovalEnabled, showGrids, permissions],
  );

  const isMapVisible = useMemo(
    () => (size || []).filter(Boolean).length === 2,
    [size],
  );

  const layersConfig = useMemo(() => {
    if (typeof config === 'function') return config(supportingLayers);
    return [].concat(config).concat(supportingLayers).filter(Boolean);
  }, [config, supportingLayers]);

  useEffect(() => {
    if (!map) return;

    setLayers((current) =>
      layersConfig.map((layer, i, all) =>
        mapLayerConfig(map, find(current, { id: layer.id }), layer, i, all),
      ),
    );
  }, [layersConfig, map]);

  const updateConfig = useCallback(
    (layerId, newConfig) =>
      setLayers((current) =>
        current.map((layer) => {
          if (layer.id !== layerId) return layer;
          return { ...layer, ...newConfig };
        }),
      ),
    [],
  );

  useEffect(() => {
    layers.forEach(cacheLayerMetadata);
  }, [layers]);

  useEffect(() => {
    if (map) {
      map.layers = layers;
      map.filters = filters;
      map.selectedFeatures = selectedFeatures;
    }
  }, [map, layers, filters, selectedFeatures]);

  const fetchLayer = useCallback(fetchLayerCallback, [
    disabledLayers,
    filters,
    extent,
    size,
    zoom,
    currentUser,
    selectedFeatures,
    layers,
  ]);

  const toggleLayer = useCallback(toggleLayerCallback, [
    fetchLayer,
    isMapVisible,
  ]);
  const abortAllCurrent = useCallback(abortAllCallback, [layers]);
  const refetchAllLayers = useDebouncedCallback(refetchAllCallback, 500);
  useEffect(disabledEffect, [zoom, layers, visibleLayers]);
  useEffect(styleSelectedEffect, [selectedFeatures, layers]);
  useEffect(clickEffect, [layers, visibleLayers, onClick]);

  useEffect(syncInitialVisibilities, [
    extent,
    layers,
    toggleLayer,
    visibilities,
  ]);

  useEffect(syncVisibilities, [
    layers,
    toggleLayer,
    visibilities,
    visibilitiesChanged,
  ]);

  useEffect(refetchAllEffect, [
    refetchAllLayers,
    extent,
    layers,
    filters,
    abortAllCurrent,
  ]);

  useEffect(() => {
    layers.forEach((layer) => {
      if (!layer.onLayer) return;
      const { onLayer, ...rest } = layer;
      onLayer(rest);
    });
  }, [layers]);

  if (!map) return null;

  function updateZIndex(layer, relativeIndex) {
    updateStyle(layer, { zIndex: 31 + layers.length - relativeIndex });
  }

  function syncInitialVisibilities() {
    if (!extent || !layers.length || hasTurnedOnVisibleLayers.current) return;
    hasTurnedOnVisibleLayers.current = true;

    layers.forEach((layer) => {
      if (layer.visible || visibilities[layer.id]) toggleLayer(layer);
    });
  }

  function syncVisibilities() {
    if (
      visibilities &&
      visibilitiesChanged &&
      hasTurnedOnVisibleLayers.current
    ) {
      Object.entries(visibilities).forEach(([id, visible]) => {
        const layer = find(layers, { id });
        if (layer && Boolean(layer.dataLayer?.style?.visible) !== visible) {
          toggleLayer(layer);
        }
      });
    }
  }

  function refetchAllEffect() {
    abortAllCurrent();
    refetchAllLayers();
    return () => refetchAllLayers.cancel();
  }

  function styleSelectedEffect() {
    styleSelectedFeatures(selectedFeatures, layers);
  }

  function clickEffect() {
    if (!onClick) return;

    const listeners = layers.map((layer) => {
      if (!CLICKABLE.includes(layer.id)) return;

      updateStyle(layer, { clickable: true });

      return layer.dataLayer.addListener('click', (event) => {
        event.feature.toGeoJson((geojson) => {
          onClick(
            layer.id,
            geojson.properties.value,
            geojson.properties,
            event.latLng,
          );
        });
      });
    });

    return () => listeners.forEach((listener) => listener && listener.remove());
  }

  function disabledEffect() {
    setDisabledLayers(
      layers
        .map((layer) => (zoom < (layer.minZoom || MIN_ZOOM) ? layer.id : false))
        .filter(Boolean),
    );
  }

  function abortAllCallback() {
    layers.forEach((layer) => {
      if (layer.currentRequest) {
        layer.currentRequest.abort();
        layer.currentParams = null;
      }
    });
  }

  async function refetchAllCallback() {
    const visibleFirst = sortBy(layers, (l) => !visibleLayers.includes(l.id));

    await Promise.all(
      visibleFirst.map(async (layer) => {
        const shouldFetch =
          visibleLayers.includes(layer.id) || layer.currentRequest;

        if (isMapVisible && shouldFetch) await fetchLayer(layer);
      }),
    );
  }

  async function toggleLayerCallback(layer) {
    const isTurningOn = !layer.dataLayer.style.visible;
    const shouldFetch = isTurningOn;
    if (isMapVisible && shouldFetch) await fetchLayer(layer);
    updateStyle(layer, { visible: isTurningOn });
    setVisibleLayers(toggle(layer));
  }

  async function fetchLayerCallback(layer) {
    if (disabledLayers.includes(layer.id)) return;
    setLoadingLayers(toggle(layer));

    const params = { filters: { ...filters }, extent, size, zoom };
    const features = await getFeatures(layer, params, currentUser?.username);

    if (!features) return setLoadingLayers(toggle(layer));

    layer.legend = features.properties?.legend;
    layer.style = features.properties?.style;
    layer.meta = features.properties;
    layer.opacityControl = !!layer.legend;

    updateStyle(layer, layer.style);

    if (features.properties?.imageUrl) {
      showImage(layer, features.properties.imageUrl);
    }

    if (features.features.length) {
      layer.selectedStyle = features.properties?.selectedStyle;
      showFeatures(layer, features);
      styleSelectedFeatures(selectedFeatures, layers);
    }

    setLoadingLayers(toggle(layer));
  }

  return {
    length: layers.length,
    map: layers.map.bind(layers),
    find: layers.find.bind(layers),
    filter: layers.filter.bind(layers),
    sort: layers.sort.bind(layers),
    forEach: layers.forEach.bind(layers),
    toggle: toggleLayer,
    updateConfig,
    updateStyle,
    updateZIndex,
    isHidden: (layer) => layer.hidden,
    isVisible: (layer) => visibleLayers.includes(layer.id),
    isDisabled: (layer) => disabledLayers.includes(layer.id),
    isLoading: (layer) => loadingLayers.includes(layer.id),
    areAnyLoading: loadingLayers.length > 0,
  };
}

function updateStyle(layer, newStyle) {
  layer.imageLayer.setStyle(newStyle);
  layer.dataLayer.setStyle({ ...layer.dataLayer.style, ...newStyle });
}

function toggle(layer) {
  return (currentState) => {
    if (!currentState.includes(layer.id)) return [...currentState, layer.id];
    return currentState.filter((k) => k !== layer.id);
  };
}

function mapLayerConfig(map, existing, layerConfig, index, all) {
  const style = { clickable: false, zIndex: 31 + all.length - index };

  return {
    ...existing,
    ...layerConfig,
    ...(existing
      ? null
      : {
          dataLayer: buildDataLayer(map, style),
          imageLayer: buildImageLayer(map, style),
        }),
  };
}

function buildDataLayer(map, style) {
  return new window.google.maps.Data({ map, style });
}

function buildImageLayer(map, style) {
  const overlay = new ImageOverlay(style);
  Object.setPrototypeOf(overlay, window.google.maps.OverlayView.prototype);
  overlay.setMap(map);
  return overlay;
}

async function getFeatures(layer, params, user) {
  if (layer.features) return layer.features;
  delete layer.error;

  if (layer.filters) {
    Object.assign(params.filters, layer.filters);
  }

  if (isEqual(layer.currentParams, params)) return;

  if (layer.currentRequest) layer.currentRequest.abort();
  layer.currentRequest = new AbortController();
  layer.currentParams = params;

  const features = await post(
    `map/features/${getLayerId(layer)}`,
    { ...params, user },
    { signal: layer.currentRequest.signal },
  ).catch((e) => {
    if (e.code !== 'ERR_CANCELED') layer.error = e;
  });

  delete layer.currentRequest;
  return features;
}

function getLayerId(layer) {
  return layer.featureId || layer.id;
}

async function cacheLayerMetadata(layer) {
  if (!hasChartColors(layer.id) && !layer.hidden && layer.id !== 'soil-grid') {
    setChartColors(layer);
    const properties = await get(`map/properties/${getLayerId(layer)}`);
    setChartColors({ ...layer, ...properties });
  }
}

function showFeatures(layer, features) {
  layer.dataLayer.forEach((f) => layer.dataLayer.remove(f));
  layer.dataLayer.addGeoJson(features).forEach((f) => {
    if (f.getProperty('style')) {
      layer.dataLayer.overrideStyle(f, f.getProperty('style'));
    }

    if (layer.getStyle) {
      layer.dataLayer.overrideStyle(f, layer.getStyle(f));
    }
  });
}

function showImage(layer, url) {
  layer.imageLayer.setImageUrl(url);
}

function styleSelectedFeatures(selected, layers) {
  Object.entries(selected).forEach(([layerId, featureValues]) => {
    const layer = layers.find((l) => layerId === l.id);

    layer?.dataLayer.forEach((f) => {
      const isSelected = featureValues.includes(f.getProperty('value'));
      layer?.dataLayer.revertStyle(f);

      if (layer.getStyle) {
        layer.dataLayer.overrideStyle(f, layer.getStyle(f, isSelected));
      } else if (isSelected) {
        layer.dataLayer.overrideStyle(f, layer.selectedStyle || {});
      } else if (f.getProperty('style')) {
        layer.dataLayer.overrideStyle(f, f.getProperty('style'));
      }
    });
  });
}
